import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import staticBlogPosts, { type BlogContentBlock, type BlogPost } from "@/data/blogPosts";
import { clampSeoTitle } from "@/lib/seoTitle";

const SITE_URL = "https://mvibeon.com";
const FEATURED_FALLBACK_SLUGS = [
  "soft-skills-training-programs-for-employees",
  "soft-skills-training-for-senior-leaders",
  "soft-skills-training-for-corporates",
];
const FEATURED_CASE_STUDY_SLUGS = [
  "senior-it-leadership-emotional-intelligence",
  "mba-students-corporate-readiness-davangere",
  "career-clarity-coaching-france",
];

export interface SEOContent {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  type?: string;
  noIndex?: boolean;
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
}

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

export interface SitePageContent {
  heroEyebrow?: string;
  heroTitle?: string;
  heroDescription?: string;
  intro?: string;
  sections?: LegalSection[];
  [key: string]: unknown;
}

export interface SitePageRecord {
  slug: string;
  title: string;
  content: SitePageContent;
  seo: SEOContent;
  isPublished?: boolean;
}

export interface CmsBlogPost extends BlogPost {
  seo?: SEOContent;
  isFeatured?: boolean;
  isPublished?: boolean;
  sortOrder?: number;
  publishAt?: string | null;
}

export interface CaseStudyOutcome {
  label: string;
  value: string;
  detail: string;
}

export interface CaseStudyRecord {
  slug: string;
  title: string;
  category: string;
  summary: string;
  image: string;
  clientLabel: string;
  engagementLabel: string;
  durationLabel: string;
  challenge: string;
  approach: string[];
  solution: string;
  outcomes: CaseStudyOutcome[];
  keyTakeaways: string[];
  testimonialQuote?: string;
  testimonialAttribution?: string;
  ctaTitle: string;
  ctaDescription: string;
  seo: SEOContent;
  isFeatured?: boolean;
  isPublished?: boolean;
  sortOrder?: number;
}

const db = supabase as any;

const getBlogCanonical = (slug: string, candidate?: string) =>
  candidate && candidate.startsWith("http") ? candidate : `${SITE_URL}/blog/${slug}`;

const toFallbackBlog = (post: BlogPost, index: number): CmsBlogPost => ({
  ...post,
  seo: {
    title: clampSeoTitle(`${post.title} | MVIBE`),
    description: post.excerpt,
    canonical: `${SITE_URL}/blog/${post.slug}`,
    ogTitle: post.title,
    ogDescription: post.excerpt,
    ogImage: post.image,
    type: "article",
  },
  isFeatured: FEATURED_FALLBACK_SLUGS.includes(post.slug),
  isPublished: true,
  sortOrder: index,
});

const fallbackBlogs = staticBlogPosts
  .map(toFallbackBlog)
  .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

const fallbackCaseStudies: CaseStudyRecord[] = [
  {
    slug: "senior-it-leadership-emotional-intelligence",
    title: "How a Senior IT Leadership Team Built Emotional Intelligence in 2 Sessions",
    category: "Corporate Training",
    summary:
      "A mid-size IT company partnered with MVIBE to help senior leaders improve team communication, reduce emotional friction, and create a healthier leadership culture during a high-pressure growth phase.",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200",
    clientLabel: "Mid-size IT company",
    engagementLabel: "Leadership workshop series",
    durationLabel: "2 focused sessions",
    challenge:
      "The organisation was seeing signs of burnout, miscommunication, and leadership fatigue among senior managers. Conversations were becoming increasingly transactional, difficult discussions were being delayed, and team members were struggling to stay aligned under pressure. The leadership team needed a practical intervention that would improve emotional awareness without feeling abstract or overly theoretical.",
    approach: [
      "Conducted two highly interactive sessions tailored for senior IT leaders managing performance pressure, cross-functional stakeholders, and team fatigue.",
      "Used emotionally intelligent leadership frameworks to help participants recognise stress triggers, communication blind spots, and the ripple effects of reactive behaviour.",
      "Introduced reflective dialogue exercises and structured peer conversations to help the group practise empathy, listening, and constructive response patterns.",
      "Included art-based stress release activities to make emotional regulation practical, memorable, and immediately usable beyond the workshop room.",
    ],
    solution:
      "MVIBE designed an experiential workshop format that blended emotional intelligence concepts, real workplace scenarios, and stress management tools into a short, action-oriented intervention. Instead of generic training, the programme focused on the daily behavioural moments that shape trust, collaboration, and leadership credibility.",
    outcomes: [
      {
        label: "Collaboration",
        value: "85%",
        detail: "of participants reported noticeable improvement in team collaboration within 30 days.",
      },
      {
        label: "Engagement",
        value: "2 sessions",
        detail: "delivered enough clarity and momentum to create new team communication habits.",
      },
      {
        label: "Leadership impact",
        value: "Higher trust",
        detail: "leaders left with concrete ways to de-escalate stress and lead conversations more intentionally.",
      },
    ],
    keyTakeaways: [
      "Emotional intelligence becomes most valuable when translated into everyday leadership behaviour.",
      "Short, well-designed interventions can create visible team culture shifts when the audience is senior and the content is highly contextual.",
      "Stress management tools are more effective when paired with communication practice, not taught in isolation.",
    ],
    testimonialQuote:
      "The sessions were practical, grounded, and surprisingly honest. Our leaders did not just understand emotional intelligence better — they began to apply it immediately in team conversations.",
    testimonialAttribution: "Programme stakeholder, IT leadership intervention",
    ctaTitle: "Want stronger leadership conversations inside your organisation?",
    ctaDescription:
      "MVIBE designs focused leadership interventions that improve communication, resilience, and team trust without adding unnecessary complexity.",
    seo: {
      title: "Senior IT Leadership Emotional Intelligence Case Study | MVIBE",
      description:
        "See how MVIBE helped a senior IT leadership team improve collaboration, emotional intelligence, and communication in just two sessions.",
      canonical: `${SITE_URL}/case-studies/senior-it-leadership-emotional-intelligence`,
      ogTitle: "Senior IT Leadership Emotional Intelligence Case Study",
      ogDescription:
        "A practical leadership workshop that improved team communication, trust, and resilience for senior IT managers.",
      ogImage: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200",
      type: "article",
    },
    isFeatured: true,
    isPublished: true,
    sortOrder: 0,
  },
  {
    slug: "mba-students-corporate-readiness-davangere",
    title: "Preparing 200+ MBA Students for Corporate Readiness in Davangere",
    category: "Campus-to-Corporate",
    summary:
      "An institution in Davangere engaged MVIBE to prepare MBA students for the transition from academics to the workplace with practical training in communication, interviews, and professional confidence.",
    image: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1200",
    clientLabel: "MBA college in Davangere",
    engagementLabel: "Campus-to-corporate training",
    durationLabel: "200+ students trained",
    challenge:
      "Students were academically capable but lacked clarity on how to present themselves in professional settings. Faculty wanted a programme that could bridge the gap between classroom learning and employer expectations, especially around communication, interview performance, and workplace readiness. The intervention needed to feel relevant, practical, and energising for a large student cohort.",
    approach: [
      "Designed a campus-to-corporate module tailored for MBA students entering placements and early-career opportunities.",
      "Covered interview readiness, personal presentation, communication basics, and professional etiquette using real corporate examples.",
      "Used high-participation activities, guided reflection, and scenario-based exercises so students could rehearse confidence before high-stakes interactions.",
      "Focused on helping participants translate existing academic strengths into language and behaviours employers could immediately recognise.",
    ],
    solution:
      "MVIBE delivered a practical employability experience rather than a lecture-based workshop. The programme turned vague ideas like professionalism and confidence into specific, repeatable behaviours students could use in interviews, group discussions, and workplace interactions.",
    outcomes: [
      {
        label: "Confidence lift",
        value: "70%",
        detail: "average increase in post-programme confidence scores reported by students.",
      },
      {
        label: "Reach",
        value: "200+",
        detail: "students participated in a shared readiness experience aligned to employer expectations.",
      },
      {
        label: "Practicality",
        value: "Action-ready",
        detail: "students left with specific tools for interviews, communication, and professional presence.",
      },
    ],
    keyTakeaways: [
      "Career readiness improves when students can practise workplace behaviour before entering a hiring process.",
      "Confidence grows faster when training is concrete, contextual, and participatory.",
      "Campus-to-corporate programmes are most effective when they connect employability skills to real business expectations.",
    ],
    testimonialQuote:
      "Students were visibly more engaged because the examples felt real. The training gave them language, structure, and confidence they could actually use.",
    testimonialAttribution: "Academic programme coordinator",
    ctaTitle: "Need a stronger campus-to-corporate programme for your students?",
    ctaDescription:
      "MVIBE delivers practical readiness workshops that help students communicate with confidence and step into professional spaces with greater clarity.",
    seo: {
      title: "MBA Students Corporate Readiness Case Study | MVIBE",
      description:
        "Learn how MVIBE prepared 200+ MBA students in Davangere for interviews, workplace communication, and corporate readiness.",
      canonical: `${SITE_URL}/case-studies/mba-students-corporate-readiness-davangere`,
      ogTitle: "MBA Students Corporate Readiness Case Study",
      ogDescription:
        "A campus-to-corporate training engagement that increased student confidence and practical workplace readiness.",
      ogImage: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1200",
      type: "article",
    },
    isFeatured: true,
    isPublished: true,
    sortOrder: 1,
  },
  {
    slug: "career-clarity-coaching-france",
    title: "From Career Stagnation to Clarity: A Working Professional’s Coaching Journey",
    category: "Life Coaching",
    summary:
      "A working professional based in France came to MVIBE feeling stuck, overwhelmed, and uncertain about next steps. Through a structured coaching journey, the client moved from procrastination to clear action.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200",
    clientLabel: "Working professional in France",
    engagementLabel: "1:1 life coaching",
    durationLabel: "6-session programme",
    challenge:
      "The client had strong potential but was caught in a cycle of indecision, procrastination, and unclear priorities. Although they wanted career movement, they lacked a grounded roadmap and were carrying limiting beliefs that made progress feel emotionally heavy. What they needed was not generic motivation, but a structured coaching process that would help them create clarity and sustain follow-through.",
    approach: [
      "Built a six-session coaching arc focused on self-awareness, decision clarity, and practical forward movement.",
      "Used guided reflection and coaching prompts to identify internal blockers, recurring patterns, and hidden assumptions shaping the client’s behaviour.",
      "Translated insight into action by breaking larger career goals into smaller, realistic commitments tied to timelines and accountability.",
      "Supported the client in reframing self-doubt and reconnecting with strengths, preferences, and a more sustainable definition of progress.",
    ],
    solution:
      "MVIBE created a coaching container that balanced emotional depth with action. The work moved beyond surface-level career advice and helped the client understand how beliefs, habits, and delayed decisions were affecting momentum. With structure and support, the client built a path that felt both ambitious and achievable.",
    outcomes: [
      {
        label: "Clarity",
        value: "8 weeks",
        detail: "to define and begin executing a tailored career action plan.",
      },
      {
        label: "Momentum",
        value: "6 sessions",
        detail: "were enough to move from hesitation into committed forward action.",
      },
      {
        label: "Confidence",
        value: "Sustainable",
        detail: "the client gained a decision-making framework they could continue using independently.",
      },
    ],
    keyTakeaways: [
      "Career clarity often comes from structured reflection plus action, not from waiting for certainty to appear on its own.",
      "When limiting beliefs are addressed directly, execution becomes lighter and more consistent.",
      "Coaching is most powerful when it helps clients build both insight and self-trust.",
    ],
    testimonialQuote:
      "I stopped circling the same thoughts and finally had a plan that felt real. The coaching helped me move from internal noise to grounded action.",
    testimonialAttribution: "Private coaching client",
    ctaTitle: "Ready to move from confusion to clarity in your own career journey?",
    ctaDescription:
      "MVIBE supports working professionals with coaching that turns reflection into action, confidence, and sustainable progress.",
    seo: {
      title: "Career Clarity Coaching Case Study | MVIBE",
      description:
        "Explore how MVIBE helped a working professional in France move from procrastination and career stagnation to a clear action plan.",
      canonical: `${SITE_URL}/case-studies/career-clarity-coaching-france`,
      ogTitle: "Career Clarity Coaching Case Study",
      ogDescription:
        "A six-session coaching journey that helped a professional build direction, confidence, and momentum.",
      ogImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200",
      type: "article",
    },
    isFeatured: true,
    isPublished: true,
    sortOrder: 2,
  },
];

const caseStudyFallbackMap = Object.fromEntries(fallbackCaseStudies.map((study) => [study.slug, study]));

export const pageFallbacks: Record<string, SitePageRecord> = {
  home: {
    slug: "home",
    title: "Home",
    content: {
      heroEyebrow: "Coaching · Training · Consulting",
      heroTitle: "Unlock Your Full Potential. Lead with Clarity. Achieve Lasting Success.",
      heroDescription:
        "MVIBE empowers individuals and organisations to cultivate inner clarity and achieve breakthrough results through personalized coaching, transformative training, and strategic consulting.",
      stats: [
        { value: 300, suffix: "+", label: "Hours of Coaching" },
        { value: 15, suffix: "+", label: "Years Corporate Experience" },
        { value: 150, suffix: "+", label: "Training Delivered" },
        { value: 7, suffix: "", label: "Professional Certifications" },
      ],
      servicesSectionEyebrow: "Coaching and Expertise",
      servicesSectionTitle: "What We Do Best",
      servicesSummaryLine: "12+ Courses & Programmes | 3,000+ Professionals Reached | 50+ Corporates & Colleges",
      servicesCtaLabel: "Explore Services",
      services: [
        {
          title: "Executive Facilitation & Leadership Training",
          desc: "We help individuals and organisations unlock clarity through coaching, training, and expert consulting — removing roadblocks, building authentic leadership, and enabling lasting growth.",
          link: "/services/companies",
        },
        {
          title: "Soft Skills Training — Campus to Corporate",
          desc: "Practical workshops with real corporate examples to build confidence, communication, and career readiness for final-year students and graduates.",
          link: "/services/companies",
        },
        {
          title: "Life Coaching for Working Professionals",
          desc: "Career clarity, burnout recovery, leadership identity, goal alignment, and work-life integration through guided one-on-one coaching sessions.",
          link: "/services/individuals",
        },
        {
          title: "Personal Branding & Image Consulting",
          desc: "Discover your authentic self and cultivate confidence through personalized style coaching, strategic use of colors, and effective body language.",
          link: "/services/individuals",
        },
      ],
      whyEyebrow: "Why Us · MVIBE Advantage",
      whyTitle: "Clarity within is the reason transformation sticks",
      whyDescription:
        "We don't deliver generic workshops. We craft personalized pathways to leadership, confidence, and lasting change — for individuals and organisations alike.",
      differentiators: [
        {
          title: "15+ Years of Corporate Insight",
          desc: "Our founder's deep IT HR leadership experience gives a rare understanding of corporate dynamics, pressures, and opportunities.",
        },
        {
          title: "Coaching-Led Facilitation",
          desc: "Every session blends reflective coaching with directive facilitation, balancing guidance with self-discovery.",
        },
        {
          title: "Tailored to Your Audience",
          desc: "CXO leadership offsites, student campus workshops, or individual coaching — each programme is adapted to the audience.",
        },
        {
          title: "Mental Fitness & Resilience Focus",
          desc: "Practical strategies for stress management, overcoming limiting beliefs, and building mental strength for demanding environments.",
        },
        {
          title: "India & UAE Presence",
          desc: "Serving professionals and organisations across India and Abu Dhabi with face-to-face and virtual delivery.",
        },
        {
          title: "Certified & Credentialed",
          desc: "ICF-aligned Coach, NLP Practitioner, ART Therapy, Certified POSH Trainer, NABET Soft Skills Trainer, City & Guild.",
        },
      ],
      founder: {
        eyebrow: "About the Founder",
        title: "Coaching and training built on 15+ years of corporate truth",
        paragraphs: [
          "MVIBE is a coaching, training, and image consulting firm founded by Mahirah (Sana Amrin). With 15+ years of experience in IT HR leadership and corporate operations, Mahirah blends real-world corporate insight with certified coaching methodology to deliver training that actually sticks.",
          "She has spent 300+ hours coaching individuals and groups, and delivered 150+ hours of training in emotional intelligence, leadership, communication, art therapy, graphology, and stress management across corporates, colleges, and hotels.",
        ],
        credentials: [
          "15+ Years of Experience in IT HR Leadership, Corporate Training, and Operations",
          "Professional Certifications: ICF, NLP, ART Therapy, NABET, POSH, City & Guild, Graphology",
        ],
        ctaLabel: "Learn More About Mahirah →",
      },
      testimonials: {
        title: "What Our Clients Experience",
        description:
          "At MVIBE, transformation isn't a promise — it's a pattern. Here's what professionals across industries and geographies say about working with Mahirah.",
        items: [
          {
            text: "Mahirah's leadership training was one of the most impactful learning experiences I've had. She guided us through each session with patience and insight, helping me understand how to lead with empathy and clarity.",
            name: "Priya",
            role: "Working Professional & Coach, Netherlands",
          },
          {
            text: "Mahirah is a true gem! Her ability to truly listen and understand, even the unspoken, helped me uncover hidden strengths and break free from self-doubt.",
            name: "Fahad",
            role: "Businessperson, UK",
          },
          {
            text: "Mahirah's ability to blend clarity, empathy, and structure makes her sessions impactful across levels — from students to senior leaders. Her corporate grounding adds to her credibility.",
            name: "Engineering College Professor",
            role: "Davangere, Karnataka",
          },
          {
            text: "Working with Mahirah was a transformative experience. She helped me clarify my career goals and motivated me to create a tailored action plan.",
            name: "Dipak L",
            role: "Working Professional, France",
          },
        ],
      },
      faqs: {
        title: "Frequently Asked Questions",
        ctaLabel: "See More FAQs →",
        items: [
          {
            q: "What is MVIBE and what services do you offer?",
            a: "MVIBE is a transformative coaching, training, and image consulting firm founded by Mahirah (Sana Amrin). We offer Soft Skills and Behavioural Training for Corporates, Campus-to-Corporate Training for Students, Life Coaching for Working Professionals, and Personal Branding through Image Consulting.",
          },
          {
            q: "Who is the founder of MVIBE and what are her qualifications?",
            a: "Mahirah (Sana Amrin) is an Executive Facilitator, Soft Skills Trainer, and ICF-aligned Life Coach with 15+ years of corporate experience in IT HR leadership and operations.",
          },
          {
            q: "Who are MVIBE's training programmes designed for?",
            a: "Our programmes serve CXOs, DGMs, and senior leadership teams; final-year students and graduate trainees; working professionals, managers, and entrepreneurs across India and the UAE.",
          },
          {
            q: "What topics does MVIBE cover in corporate soft skills training?",
            a: "We cover Executive Presence and Leadership Impact, Strategic Communication and Influence, Decision-Making Under Pressure, Emotional Intelligence for Senior Leaders, Stakeholder Management and Difficult Conversations, Leading Through Change and Burnout, and Systems Thinking.",
          },
          {
            q: "How can I book a discovery call with MVIBE?",
            a: "You can book a complimentary discovery call through our Contact page, or reach us directly via WhatsApp, email, or LinkedIn.",
          },
        ],
      },
      contactCta: {
        title: "Ready to Find Clarity in Your Next Move?",
        description:
          "Whether it's a leadership breakthrough for your team or personal coaching for your own growth — it starts here.",
        benefits: [
          "300+ Coaching Hours",
          "India & UAE Presence",
          "7 Professional Certifications",
          "Clarity Within. Success Beyond.",
        ],
        buttonLabel: "Get Started →",
        formTitle: "Book a Discovery Call",
        formDescription: "Tell us about your need — Mahirah will respond within 24 hours.",
        submitLabel: "Get Started",
        subjectOptions: [
          "Individual Coaching",
          "Corporate Training",
          "Image Consulting",
          "Campus-to-Corporate Training",
          "Handwriting Analysis",
          "ART Therapy for Stress Relief & Confidence",
          "Something Else",
          "General Inquiry",
        ],
      },
    },
    seo: {
      title: "Executive Coaching & Soft Skills Training in Dubai | MVIBE",
      description:
        "MVIBE delivers ICF-certified executive coaching, corporate soft skills training, and campus-to-corporate programmes in Dubai, led by Mahirah with 15+ years in IT HR leadership.",
      canonical: `${SITE_URL}/`,
      ogTitle: "Executive Coaching & Soft Skills Training in Dubai | MVIBE",
      ogDescription:
        "ICF-certified executive coaching, corporate soft skills training, and campus-to-corporate programmes in Dubai and India, led by Mahirah.",
      type: "website",
      schema: {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "MVIBE",
        url: SITE_URL,
        slogan: "Clarity Within. Success Beyond.",
        email: "mvibeon@gmail.com",
        telephone: "+91 78998 04670",
        areaServed: ["India", "United Arab Emirates"],
      },
    },
  },
  about: {
    slug: "about",
    title: "About Us",
    content: {
      heroTitle: "Our Story: The Journey to Clarity Within. Success Beyond.",
      heroDescription: "Meet the founder and the philosophy behind every transformation at MVIBE",
      founderName: "Mahirah (Sana Amrin)",
      founderBadges: [
        "Founder – MViBE",
        "Co-founder – Bold Bridge",
        "Life Coach",
        "Executive Facilitator",
        "Soft Skills Trainer",
        "Art Therapist",
        "Graphologist",
      ],
      founderParagraphs: [
        "As an IT leader, I navigated intense corporate landscapes, where I learned that true success for individuals and organisations hinges on well-being and authentic leadership. This realization sparked MVIBE.",
        "At MVIBE, our tagline 'Clarity Within. Success Beyond.' is our promise. We empower you to cultivate inner clarity through personalized coaching and transformative training.",
        "Because I've been there, I understand the challenges you face. My commitment is to help you unlock your full potential, build unshakeable confidence, and thrive.",
      ],
      founderQuote: "Clarity Within. Success Beyond.",
      visionCards: [
        {
          title: "Vision",
          text: "Empowering individuals and organisations to achieve clarity, resilience, and lasting transformation that goes beyond the surface.",
        },
        {
          title: "Mission",
          text: "To provide personalized coaching, transformative training, and strategic consulting that creates measurable breakthroughs in leadership, communication, and well-being.",
        },
        {
          title: "Values",
          text: "Authenticity · Empowerment · Empathy · Growth · Integrity",
        },
      ],
      certificationsTitle: "Certifications & Professional Training",
      certifications: [
        "ICF-aligned Life Coach",
        "NLP Practitioner",
        "ART Therapy Practitioner",
        "Certified POSH Trainer",
        "NABET Soft Skills Trainer",
        "City & Guild Trainer",
        "Masters in Graphology (Handwriting Analysis)",
      ],
      methodologyTitle: "Our Facilitation & Coaching Methodology",
      methodology: [
        "Coaching-led facilitation with clear outcomes",
        "Behavioural and emotional intelligence frameworks",
        "Experiential learning, reflection, and dialogue",
        "Selective integration of therapeutic tools (used contextually, not foregrounded)",
      ],
      corporateBackgroundTitle: "Corporate Background & Credibility",
      corporateBackground: [
        "15+ years of experience in IT HR leadership and operations",
        "Direct exposure to senior leadership environments and organizational decision-making",
        "Strong understanding of corporate systems, people dynamics, and performance pressures",
      ],
      ctaTitle: "Ready to experience the MVIBE difference?",
      ctaButtonLabel: "Book a Discovery Call",
    },
    seo: {
      title: "About MVIBE — Meet Founder Mahirah (Sana Amrin)",
      description:
        "Learn about Mahirah (Sana Amrin), the founder of MVIBE — Executive Facilitator, ICF-aligned Life Coach, and Soft Skills Trainer with 15+ years of IT HR leadership experience.",
      canonical: `${SITE_URL}/about`,
      type: "website",
    },
  },
  "services-individuals": {
    slug: "services-individuals",
    title: "Services for Individuals",
    content: {
      heroTitle: "Individual Coaching & Personal Branding: Cultivate Clarity Within.",
      heroDescription: "Personalized coaching for career clarity, confidence, and personal transformation.",
      problemsTitle: "Challenges You May Be Facing",
      problems: [
        {
          title: "Feeling Stuck & Overwhelmed",
          desc: "We provide tools to untangle thoughts, discover actionable paths, and gain clarity in your career or personal life.",
        },
        {
          title: "Lacking Authentic Confidence",
          desc: "We empower you to cultivate unshakeable confidence and a personal brand that reflects your strengths.",
        },
        {
          title: "Stress, Burnout & Compromised Well-being",
          desc: "Practical strategies and creative outlets for stress management, fostering mental fitness and resilience.",
        },
        {
          title: "Navigating Change & Uncertainty",
          desc: "We equip you with emotional intelligence and adaptive skills to thrive amidst evolving environments.",
        },
      ],
      coachingTitle: "Life Coaching for Working Professionals",
      coachingAudience: "Individual contributors, managers, entrepreneurs, professionals",
      coachingFocus: [
        "Career Clarity & Direction",
        "Stress, Burnout & Resilience",
        "Leadership Identity & Self-Confidence",
        "Goal Setting & Decision Alignment",
        "Work-Life Integration",
      ],
      coachingQuote:
        "Guided by 'Clarity Within. Success Beyond.', Mahirah helps working professionals move from overwhelm to clarity, enabling sustainable performance and personal alignment.",
      brandingTitle: "Personal Branding through Image Consulting",
      brandingDescription:
        "Discover your authentic self and cultivate unshakeable confidence through personalized style coaching, strategic use of colors, and effective body language, including wardrobe curation and the Capsule Wardrobe Challenge.",
      brandingIncludes: [
        "Personalized style coaching",
        "Strategic use of colors",
        "Body language mastery",
        "Wardrobe curation",
      ],
      journeyTitle: "Your Journey With Us",
      journey: [
        {
          title: "Discovery Call",
          desc: "A complimentary conversation to understand your goals and challenges",
        },
        {
          title: "Personalized Plan",
          desc: "A tailored coaching roadmap designed for your unique situation",
        },
        {
          title: "Transformation",
          desc: "Guided sessions moving you from overwhelm to clarity and confidence",
        },
      ],
      ctaTitle: "Book Your Complimentary Discovery Call",
      ctaButtonLabel: "Get Started →",
    },
    seo: {
      title: "Life Coaching & Personal Branding for Individuals — MVIBE",
      description:
        "Personalized life coaching, career clarity sessions, and personal branding through image consulting by certified coach Mahirah.",
      canonical: `${SITE_URL}/services/individuals`,
      type: "website",
    },
  },
  "services-companies": {
    slug: "services-companies",
    title: "Services for Organisations",
    content: {
      heroTitle: "Corporate Training & Consulting: Driving Success Beyond.",
      heroDescription: "Tailored soft skills training and strategic consulting for today's dynamic workplace.",
      problemsTitle: "Challenges Your Organization May Face",
      problems: [
        {
          title: "Communication Gaps",
          desc: "Misaligned teams, unclear messaging, and ineffective collaboration across departments.",
        },
        {
          title: "Employee Burnout",
          desc: "Rising stress levels, declining productivity, and disengaged employees impacting performance.",
        },
        {
          title: "Change & Uncertainty",
          desc: "Difficulty navigating organizational transitions, restructuring, and evolving market demands.",
        },
        {
          title: "Leadership Gaps",
          desc: "Lack of emotionally intelligent leadership, affecting team morale and decision-making.",
        },
      ],
      corporateFocusTitle: "Soft Skills & Behavioural Skills and Systems Thinking for Corporates",
      corporateAudienceLabel: "CXOs, DGMs, Senior Leadership Teams",
      corporateDeliveryLabel: "Face-to-face | 3–4 hour modules or leadership offsites",
      corporateFocus: [
        "Executive Presence & Leadership Impact",
        "Strategic Communication & Influence",
        "Decision-Making Under Pressure",
        "Emotional Intelligence for Senior Leaders",
        "Stakeholder Management & Difficult Conversations",
        "Leading Through Change, Ambiguity & Burnout",
      ],
      corporateNote:
        "Business-contextual, discussion-driven, case-based learning, peer dialogue. Balances reflective coaching with directive facilitation. Designed for senior leadership maturity and pace.",
      campusFocusTitle: "Soft Skills Training for Students (Campus-to-Corporate)",
      campusAudienceLabel: "Final-year students, early-career professionals, graduate trainees",
      campusDeliveryLabel: "Face-to-face | 3–4 hour modules",
      campusFocus: [
        "Corporate Communication & Professional Etiquette",
        "Public Speaking & Presentation Skills (including Elevator Pitch)",
        "Interview Readiness & Business Writing",
        "Decision-Making & Problem Solving",
        "Time Management & Workplace Behaviour",
        "Confidence Building & Career Mindset",
      ],
      campusNote:
        "Practical, structured, application-oriented with real corporate examples from IT and HR leadership.",
      programmesTitle: "Sample Programmes & Workshops",
      sampleProgrammes: [
        "Stress Management through Colours & Art",
        "Systems Thinking for Professionals",
        "Leadership Branding & Executive Presence",
        "Leading Through Change: Emotional Intelligence & Resilience",
        "The Emotional Canvas for Creative Release & Stress Reduction",
      ],
      whyPartnerTitle: "Why Partner with MVIBE?",
      whyPartner: [
        {
          title: "IT & HR Leadership Background",
          desc: "Founder brings 15+ years of corporate IT and HR experience to every engagement.",
        },
        {
          title: "Personalized Approach",
          desc: "Every programme is tailored to your organization's unique challenges and culture.",
        },
        {
          title: "Measurable Results",
          desc: "We track outcomes and provide post-training feedback to ensure lasting impact.",
        },
        {
          title: "Certified Expertise",
          desc: "ICF-certified coaching combined with image consulting and art therapy credentials.",
        },
      ],
      benefitsTitle: "What Your Organisation Gains",
      benefits: [
        "Enhanced employee well-being, reduced burnout",
        "Improved team dynamics and communication",
        "Emotionally intelligent and resilient leaders",
        "Boosted productivity and engagement",
        "Support through change and transition",
      ],
      ctaTitle: "Let's Discuss a Tailored Training Solution",
      ctaButtonLabel: "Get Started →",
    },
    seo: {
      title: "Corporate Training & Leadership Consulting — MVIBE",
      description:
        "Tailored corporate training in emotional intelligence, leadership development, systems thinking, and campus-to-corporate readiness.",
      canonical: `${SITE_URL}/services/companies`,
      type: "website",
    },
  },
  contact: {
    slug: "contact",
    title: "Contact Us",
    content: {
      heroTitle: "Let's Connect: Start Your Journey to Clarity Within. Success Beyond.",
      heroDescription:
        "Whether you're an individual seeking coaching or an organisation looking for training solutions — your next breakthrough begins with a conversation.",
      formTitle: "Contact Us",
      formDescription: "Tell us about your need — Mahirah will respond within 24 hours.",
      submitLabel: "Send Message",
      subjectOptions: [
        "Individual Coaching",
        "Corporate Training",
        "Image Consulting",
        "Campus-to-Corporate Training",
        "Handwriting Analysis",
        "ART Therapy for Stress Relief & Confidence",
        "Something Else",
        "General Inquiry",
      ],
      contactTitle: "Get in Touch",
      followTitle: "Follow Us",
      visitTitle: "Visit Us",
      locationTitle: "Our Location",
      quickChatTitle: "Prefer a quick chat? Reach us on WhatsApp for faster response.",
      indiaPhone: "+91 78998 04670",
      uaePhone: "+971 55 283 2143",
      email: "mvibeon@gmail.com",
      linkedinLabel: "LinkedIn",
      linkedinUrl: "https://www.linkedin.com/company/mvibeon-training-coaching/",
      instagramLabel: "@mvibe_on",
      instagramUrl: "https://www.instagram.com/mvibe_on?igsh=ZjJmNGZnZXNnYWJr",
      address: "Building 12 Bawabat Al Sharq, Baniyas, Abu Dhabi, United Arab Emirates",
      whatsappIndiaLabel: "WhatsApp India",
      whatsappUaeLabel: "WhatsApp UAE",
      whatsappIndiaUrl: "https://wa.me/917899804670",
      whatsappUaeUrl: "https://wa.me/971552832143",
      emailButtonLabel: "Email Directly",
    },
    seo: {
      title: "Contact Us — MVIBE",
      description:
        "Get in touch with MVIBE for coaching, corporate training, or consulting. Book a discovery call and start your transformation journey.",
      canonical: `${SITE_URL}/contact`,
      type: "website",
    },
  },
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    content: {
      intro:
        "This Privacy Policy explains how MVIBE collects, uses, stores, and protects personal information when you visit our website, enquire about our services, or engage with our coaching, training, and consulting offerings.",
      sections: [
        {
          heading: "Information We Collect",
          paragraphs: [
            "We may collect personal details you choose to provide through contact forms, enquiry forms, email, phone, WhatsApp, or other direct communication channels. This may include your name, email address, phone number, company name, role, and any information you share about your goals or requirements.",
            "We may also collect limited technical information such as browser type, device information, referring pages, and general website usage data to improve website performance, security, and user experience.",
          ],
        },
        {
          heading: "How We Use Your Information",
          paragraphs: [
            "We use your information to respond to enquiries, schedule discovery calls, deliver coaching or training services, improve our website and service experience, and send relevant follow-up communication related to your enquiry or engagement.",
            "We do not sell your personal information. We only use it for legitimate business purposes connected to MVIBE’s services, operations, and client support.",
          ],
        },
        {
          heading: "Legal Basis and Consent",
          paragraphs: [
            "When you voluntarily submit your information, you consent to MVIBE using that information to contact you and provide the services or information you requested.",
            "Where required by law, we rely on consent, contractual necessity, legitimate business interests, or legal obligations as the basis for processing personal information.",
          ],
        },
        {
          heading: "Sharing of Information",
          paragraphs: [
            "We may share information with trusted service providers who support website hosting, communications, scheduling, analytics, or operational processes, but only to the extent reasonably necessary for those services.",
            "We may also disclose information if required by law, regulation, legal process, or to protect the rights, safety, and security of MVIBE, its users, or others.",
          ],
        },
        {
          heading: "Data Retention and Security",
          paragraphs: [
            "We retain personal information only for as long as needed to fulfil the purpose for which it was collected, comply with legal obligations, resolve disputes, and enforce agreements.",
            "We take reasonable administrative, technical, and organisational measures to protect the information we hold. However, no online system can be guaranteed to be completely secure.",
          ],
        },
        {
          heading: "Your Rights",
          paragraphs: [
            "Depending on your location, you may have rights to request access to, correction of, deletion of, or restriction of the personal information we hold about you. You may also object to certain processing or withdraw consent where consent is the basis for processing.",
            "To exercise these rights, contact us at mvibeon@gmail.com. We may need to verify your identity before processing certain requests.",
          ],
        },
        {
          heading: "External Links and Children’s Privacy",
          paragraphs: [
            "Our website may include links to third-party websites or social platforms. We are not responsible for the privacy practices, content, or security of those external services.",
            "Our website and services are not directed to children under 18, and we do not knowingly collect personal information from children through this website.",
          ],
        },
        {
          heading: "Contact and Updates",
          paragraphs: [
            "If you have any questions about this Privacy Policy or how your information is handled, please contact MVIBE at mvibeon@gmail.com or through the Contact page on this website.",
            "We may update this Privacy Policy from time to time. Updates will be posted on this page with the latest effective version reflected here.",
          ],
        },
      ],
    },
    seo: {
      title: "Privacy Policy — MVIBE",
      description:
        "Read MVIBE’s privacy policy to understand how we collect, use, protect, and retain information shared through our website and services.",
      canonical: `${SITE_URL}/privacy`,
      type: "article",
    },
  },
  terms: {
    slug: "terms",
    title: "Terms of Service",
    content: {
      intro:
        "These Terms of Service govern your use of the MVIBE website and any coaching, consulting, facilitation, or training services you enquire about, access, or purchase through MVIBE.",
      sections: [
        {
          heading: "Acceptance of Terms",
          paragraphs: [
            "By accessing this website or engaging with MVIBE’s services, you agree to these Terms of Service and any additional policies referenced on this website. If you do not agree, please do not use the website or services.",
            "You are responsible for ensuring that your use of the website complies with applicable local laws and regulations.",
          ],
        },
        {
          heading: "Website Content and Intellectual Property",
          paragraphs: [
            "All content on this website, including text, branding, graphics, frameworks, downloads, and training-related materials, is owned by or licensed to MVIBE unless otherwise stated.",
            "You may not reproduce, distribute, modify, republish, or commercially exploit website content without prior written permission from MVIBE.",
          ],
        },
        {
          heading: "Service Information and Enquiries",
          paragraphs: [
            "Information on this website is provided for general informational and marketing purposes. Submission of an enquiry form or discovery call request does not guarantee acceptance of a project, engagement, or coaching relationship.",
            "Any timelines, outcomes, testimonials, case studies, or examples shown on the website are illustrative and do not guarantee identical results.",
          ],
        },
        {
          heading: "Client Responsibilities",
          paragraphs: [
            "Clients and participants are expected to provide accurate information, communicate respectfully, and participate in good faith in coaching, training, and consulting engagements.",
            "You remain responsible for your own decisions, actions, implementation, business outcomes, and personal circumstances. MVIBE provides support, structure, and facilitation, but does not guarantee specific commercial, career, or personal outcomes.",
          ],
        },
        {
          heading: "Payments, Scheduling, and Cancellations",
          paragraphs: [
            "Specific commercial terms, fees, payment milestones, scheduling details, and cancellation conditions may be agreed separately in a proposal, invoice, email confirmation, or service agreement.",
            "Unless otherwise agreed in writing, MVIBE reserves the right to reschedule sessions or refuse service where necessary for operational, safety, or professional reasons.",
          ],
        },
        {
          heading: "Limitations of Liability",
          paragraphs: [
            "To the fullest extent permitted by law, MVIBE is not liable for indirect, incidental, consequential, special, or exemplary damages arising from use of this website or services.",
            "Website content is provided on an as-is basis without warranties of uninterrupted availability, error-free operation, or suitability for a specific purpose.",
          ],
        },
        {
          heading: "External Services and Links",
          paragraphs: [
            "This website may include links to third-party tools, social media platforms, or external websites. MVIBE is not responsible for the content, practices, or availability of those third-party services.",
            "Your use of external websites or services is governed by the terms and policies of those third parties.",
          ],
        },
        {
          heading: "Changes and Contact",
          paragraphs: [
            "MVIBE may update these Terms of Service from time to time. Continued use of the website after updates are posted constitutes acceptance of the revised Terms.",
            "If you have questions about these Terms, please contact mvibeon@gmail.com before using the website or engaging with our services.",
          ],
        },
      ],
    },
    seo: {
      title: "Terms of Service — MVIBE",
      description:
        "Review MVIBE’s terms of service covering website use, service enquiries, intellectual property, liability, and engagement expectations.",
      canonical: `${SITE_URL}/terms`,
      type: "article",
    },
  },
  cookies: {
    slug: "cookies",
    title: "Cookie Policy",
    content: {
      intro:
        "This Cookie Policy explains how MVIBE may use cookies and similar technologies to understand website usage, maintain site performance, and improve user experience.",
      sections: [
        {
          heading: "What Cookies Are",
          paragraphs: [
            "Cookies are small text files stored on your device when you visit a website. They can help websites remember preferences, maintain functionality, and collect general usage insights.",
          ],
        },
        {
          heading: "How MVIBE Uses Cookies",
          paragraphs: [
            "MVIBE may use essential cookies for core website functionality and may use analytics-related tools to understand page performance, visitor behaviour, and content effectiveness.",
            "If additional analytics, advertising, or embedded third-party tools are added in the future, this policy may be updated to reflect those technologies.",
          ],
        },
        {
          heading: "Your Choices",
          paragraphs: [
            "Most browsers allow you to control or disable cookies through browser settings. Disabling certain cookies may affect website functionality or performance.",
            "You can also clear stored cookies from your browser at any time.",
          ],
        },
      ],
    },
    seo: {
      title: "Cookie Policy — MVIBE",
      description:
        "Learn how MVIBE uses cookies and similar technologies to support essential site functionality and improve website performance.",
      canonical: `${SITE_URL}/cookies`,
      type: "article",
    },
  },
  disclaimer: {
    slug: "disclaimer",
    title: "Website Disclaimer",
    content: {
      intro:
        "The information on the MVIBE website is provided for general informational purposes only and should not be treated as legal, medical, mental health, financial, or other regulated professional advice.",
      sections: [
        {
          heading: "No Professional or Guaranteed Outcome Advice",
          paragraphs: [
            "Coaching, training, and consulting content is designed to support learning, reflection, growth, and professional development. It is not a substitute for therapy, psychiatric care, medical care, legal counsel, or financial advice.",
            "Any examples, testimonials, or case studies shown on this website are illustrative and do not guarantee the same outcomes for every individual or organisation.",
          ],
        },
        {
          heading: "Use at Your Own Discretion",
          paragraphs: [
            "You are responsible for evaluating the relevance of any information, insights, or recommendations shared through this website before acting on them.",
            "MVIBE is not responsible for decisions made solely on the basis of publicly available website content.",
          ],
        },
        {
          heading: "External Content",
          paragraphs: [
            "This website may reference external resources, tools, or platforms. MVIBE does not control or endorse all third-party content and is not responsible for its accuracy or availability.",
          ],
        },
      ],
    },
    seo: {
      title: "Website Disclaimer — MVIBE",
      description:
        "Read the MVIBE website disclaimer covering informational content, external links, and limits on professional or guaranteed-outcome advice.",
      canonical: `${SITE_URL}/disclaimer`,
      type: "article",
    },
  },
};

const mergePageRecord = (slug: string, row: any): SitePageRecord => {
  const fallback = pageFallbacks[slug] ?? {
    slug,
    title: row?.title ?? slug,
    content: {},
    seo: {
      title: row?.title ?? slug,
      description: "",
    },
  };

  return {
    slug,
    title: row?.title ?? fallback.title,
    content: {
      ...fallback.content,
      ...(row?.content ?? {}),
    },
    seo: {
      ...fallback.seo,
      ...(row?.seo ?? {}),
    },
    isPublished: row?.is_published ?? true,
  };
};

/** Normalise a content block from API/DB format (uses `data` key) to renderer format */
const normalizeContentBlock = (block: any): BlogContentBlock | null => {
  if (!block || !block.type) return null;
  const t = block.type as string;

  // A list must always come out with an items[] array — the renderer maps over it directly.
  // Some rows store the entries under `content` instead, which the canonical check below
  // waves through untouched, leaving items undefined and blanking the whole post.
  if (t === "list") {
    const raw = block.items ?? block.content ?? block.data;
    const items = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
    return {
      type: "list",
      items: items.map((item: any) => (typeof item === "string" ? item : String(item?.text ?? item ?? ""))),
      ordered: block.ordered ?? block.data?.style === "ordered",
    } as any;
  }

  // Already in canonical format (has `content`, `items`, etc.)
  if (block.content || block.items || block.question) return block as BlogContentBlock;

  // Handle `data`-key format from Apps Script
  const d = block.data;
  if (d === undefined && block.text !== undefined) {
    // alternate flat format: { type, text, attribution? }
    if (t === "paragraph" || t === "heading") return { type: t, content: block.text } as any;
    if (t === "quote") return { type: "quote", content: block.text, attribution: block.attribution } as any;
  }
  if (d === undefined) return block as BlogContentBlock;

  switch (t) {
    case "paragraph":
    case "heading":
      return { type: t, content: typeof d === "string" ? d : "" } as any;
    case "list":
      return {
        type: "list",
        items: Array.isArray(d) ? d : (d?.items ?? []),
        ordered: d?.style === "ordered",
      } as any;
    case "highlights":
      return {
        type: "highlights",
        title: d?.title ?? "Key Highlights",
        items: Array.isArray(d?.items)
          ? d.items.map((item: any) =>
              typeof item === "string" ? { title: item, description: "" } : item
            )
          : [],
      } as any;
    case "faq":
      return {
        type: "faq",
        title: d?.title ?? "FAQs",
        items: Array.isArray(d) ? d : (d?.items ?? []),
      } as any;
    case "quote":
      return {
        type: "quote",
        content: typeof d === "string" ? d : (d?.text ?? ""),
        attribution: d?.author ?? d?.attribution,
      } as any;
    default:
      return block as BlogContentBlock;
  }
};

const normalizeContentBlocks = (raw: any): BlogContentBlock[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeContentBlock).filter(Boolean) as BlogContentBlock[];
};

const normalizeBlogRow = (row: any, fallback?: CmsBlogPost): CmsBlogPost => ({
  slug: row.slug,
  title: row.title,
  category: row.category,
  date: row.date_label,
  publishedAt: row.publish_at ?? row.created_at ?? fallback?.publishedAt ?? "2026-01-01",
  readTime: row.read_time,
  excerpt: row.excerpt,
  image: row.image,
  content: normalizeContentBlocks(row.content).length
    ? normalizeContentBlocks(row.content)
    : (fallback?.content ?? []),
  seo: (() => {
    const merged = { ...(fallback?.seo ?? {}), ...(row.seo ?? {}) };
    merged.title = clampSeoTitle(merged.title ?? `${row.title} | MVIBE`);
    merged.canonical = getBlogCanonical(row.slug, merged.canonical);
    return merged;
  })(),
  isFeatured: row.is_featured ?? fallback?.isFeatured ?? false,
  isPublished: row.is_published ?? true,
  sortOrder: row.sort_order ?? fallback?.sortOrder ?? 0,
  publishAt: row.publish_at ?? null,
});

const normalizeCaseStudyRow = (row: any, fallback?: CaseStudyRecord): CaseStudyRecord => ({
  slug: row.slug,
  title: row.title,
  category: row.category,
  summary: row.summary,
  image: row.image,
  clientLabel: row.client_label ?? fallback?.clientLabel ?? "",
  engagementLabel: row.engagement_label ?? fallback?.engagementLabel ?? "",
  durationLabel: row.duration_label ?? fallback?.durationLabel ?? "",
  challenge: row.challenge,
  approach: Array.isArray(row.approach) ? row.approach : fallback?.approach ?? [],
  solution: row.solution,
  outcomes: Array.isArray(row.outcomes) ? row.outcomes : fallback?.outcomes ?? [],
  keyTakeaways: Array.isArray(row.key_takeaways) ? row.key_takeaways : fallback?.keyTakeaways ?? [],
  testimonialQuote: row.testimonial_quote ?? fallback?.testimonialQuote,
  testimonialAttribution: row.testimonial_attribution ?? fallback?.testimonialAttribution,
  ctaTitle: row.cta_title ?? fallback?.ctaTitle ?? "Ready to create a similar transformation?",
  ctaDescription:
    row.cta_description ??
    fallback?.ctaDescription ??
    "Let’s explore the right coaching, facilitation, or training approach for your team or career stage.",
  seo: (() => {
    const merged = { ...(fallback?.seo ?? {}), ...(row.seo ?? {}) };
    if (!merged.canonical || !merged.canonical.startsWith("http")) {
      merged.canonical = `${SITE_URL}/case-studies/${row.slug}`;
    }
    return merged;
  })(),
  isFeatured: row.is_featured ?? fallback?.isFeatured ?? false,
  isPublished: row.is_published ?? true,
  sortOrder: row.sort_order ?? fallback?.sortOrder ?? 0,
});

const parseDate = (value?: string) => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

const sortNewestFirst = (posts: CmsBlogPost[]): CmsBlogPost[] =>
  [...posts].sort((a, b) => {
    const diff = parseDate(b.publishedAt) - parseDate(a.publishedAt);
    return diff !== 0 ? diff : a.slug.localeCompare(b.slug);
  });

const mergeBlogs = (rows: any[] | null | undefined): CmsBlogPost[] => {
  if (!rows?.length) {
    return sortNewestFirst(fallbackBlogs);
  }

  const fallbackMap = new Map(fallbackBlogs.map((post) => [post.slug, post]));
  const dbPosts = rows.map((row) => normalizeBlogRow(row, fallbackMap.get(row.slug)));
  return sortNewestFirst(dbPosts);
};

const mergeCaseStudies = (rows: any[] | null | undefined): CaseStudyRecord[] => {
  if (!rows?.length) {
    return [...fallbackCaseStudies].sort(
      (a, b) => Number(b.isFeatured) - Number(a.isFeatured) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );
  }

  const fallbackMap = new Map(fallbackCaseStudies.map((study) => [study.slug, study]));
  const dbStudies = rows.map((row) => normalizeCaseStudyRow(row, fallbackMap.get(row.slug)));
  const dbSlugs = new Set(dbStudies.map((study) => study.slug));
  const missingFallbacks = fallbackCaseStudies.filter((study) => !dbSlugs.has(study.slug));

  return [...dbStudies, ...missingFallbacks].sort(
    (a, b) => Number(b.isFeatured) - Number(a.isFeatured) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
};

const fallbackBlogMap = Object.fromEntries(
  fallbackBlogs.map((post) => [post.slug, post])
) as Record<string, CmsBlogPost>;

const buildBlogPostSchema = (post: CmsBlogPost) => {
  const articleUrl = post.seo?.canonical ?? `${SITE_URL}/blog/${post.slug}`;
  const publishedDate = post.publishAt ?? post.publishedAt;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.seo?.ogTitle ?? post.title,
    description: post.seo?.description ?? post.excerpt,
    image: post.seo?.ogImage ?? post.image,
    mainEntityOfPage: articleUrl,
    url: articleUrl,
    datePublished: publishedDate,
    dateModified: publishedDate,
    author: {
      "@type": "Person",
      name: "Mahirah",
    },
    publisher: {
      "@type": "Organization",
      name: "MVIBE",
      url: SITE_URL,
    },
  };
};

export const useSitePage = (slug: string) =>
  useQuery<SitePageRecord>({
    queryKey: ["site-page", slug],
    initialData: pageFallbacks[slug],
    queryFn: async () => {
      const { data, error } = await db
        .from("site_pages")
        .select("slug, title, content, seo, is_published")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (!data) {
        return pageFallbacks[slug];
      }

      return mergePageRecord(slug, data);
    },
  });

export const usePublishedBlogPosts = () =>
  useQuery<CmsBlogPost[]>({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data, error } = await db
        .from("blog_posts")
        .select("slug, title, category, date_label, read_time, excerpt, image, content, seo, is_featured, is_published, sort_order, publish_at, created_at")
        .eq("is_published", true)
        .or(`publish_at.is.null,publish_at.lte.${nowIso}`)
        .order("publish_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return mergeBlogs(data);
    },
  });

export const useBlogPost = (slug: string) =>
  useQuery<CmsBlogPost | undefined>({
    queryKey: ["blog-post", slug],
    initialData: fallbackBlogMap[slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data, error } = await db
        .from("blog_posts")
        .select("slug, title, category, date_label, read_time, excerpt, image, content, seo, is_featured, is_published, sort_order, publish_at, created_at")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (!data) {
        return fallbackBlogMap[slug];
      }

      const post = normalizeBlogRow(data, fallbackBlogMap[slug]);

      return {
        ...post,
        seo: {
          ...post.seo,
          title: clampSeoTitle(post.seo?.title ?? `${post.title} | MVIBE`),
          description: post.seo?.description ?? post.excerpt,
          canonical: getBlogCanonical(post.slug, post.seo?.canonical),
          ogTitle: post.seo?.ogTitle ?? post.title,
          ogDescription: post.seo?.ogDescription ?? post.excerpt,
          ogImage: post.seo?.ogImage ?? post.image,
          type: post.seo?.type ?? "article",
          schema: post.seo?.schema ?? buildBlogPostSchema(post),
        },
      };
    },
  });

export const usePublishedCaseStudies = () =>
  useQuery<CaseStudyRecord[]>({
    queryKey: ["case-studies"],
    initialData: fallbackCaseStudies,
    queryFn: async () => {
      const { data, error } = await db
        .from("case_studies")
        .select(
          "slug, title, category, summary, image, client_label, engagement_label, duration_label, challenge, approach, solution, outcomes, key_takeaways, testimonial_quote, testimonial_attribution, cta_title, cta_description, seo, is_featured, is_published, sort_order"
        )
        .eq("is_published", true)
        .order("is_featured", { ascending: false })
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return mergeCaseStudies(data);
    },
  });

export const useCaseStudy = (slug: string) =>
  useQuery<CaseStudyRecord | undefined>({
    queryKey: ["case-study", slug],
    initialData: caseStudyFallbackMap[slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data, error } = await db
        .from("case_studies")
        .select(
          "slug, title, category, summary, image, client_label, engagement_label, duration_label, challenge, approach, solution, outcomes, key_takeaways, testimonial_quote, testimonial_attribution, cta_title, cta_description, seo, is_featured, is_published, sort_order"
        )
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (!data) {
        return caseStudyFallbackMap[slug];
      }

      return normalizeCaseStudyRow(data, caseStudyFallbackMap[slug]);
    },
  });

export const getPageFallbacksForAdmin = () => pageFallbacks;
export const getFallbackBlogsForAdmin = () => fallbackBlogs;
export const getFallbackCaseStudiesForAdmin = () => fallbackCaseStudies;
